// utils/candidateScoreCalculations.js
import { CM_PRIMARY_ID, SE_COMMITTEE_ID, ELECTION_TYPE } from './constants';

/**
 * Helper function to filter candidate scores
 * @param {number} candidateId - The candidate's ID
 * @param {Array} allScores - Array of all candidate scores
 * @returns {Array} - Filtered scores for the candidate
 */
const filterCandidateScores = (candidateId, allScores) =>
  allScores.filter((score) => {
    const attributes = score.attributes || score;
    return (
      attributes.candidateId === candidateId &&
      attributes.totalScore !== null &&
      attributes.totalScore !== undefined
    );
  });

/**
 * Calculate average score for a candidate
 * @param {number} candidateId - The candidate's ID
 * @param {Array} allScores - Array of all candidate scores
 * @returns {number|null} - Average score or null if no scores
 */
export const calculateAverageScore = (candidateId, allScores) => {
  const candidateScores = filterCandidateScores(candidateId, allScores);

  if (candidateScores.length === 0) return null;

  const total = candidateScores.reduce((sum, score) => {
    const attributes = score.attributes || score;
    return sum + (attributes.totalScore || 0);
  }, 0);

  return total / candidateScores.length;
};

/**
 * Get the count of scores for a candidate
 * @param {number} candidateId - The candidate's ID
 * @param {Array} allScores - Array of all candidate scores
 * @returns {number} - Count of scores
 */
export const getScoreCount = (candidateId, allScores) => {
  const candidateScores = filterCandidateScores(candidateId, allScores);
  return candidateScores.length;
};

/**
 * Calculate average score and count for a candidate
 * @param {number} candidateId - The candidate's ID
 * @param {Array} allScores - Array of all candidate scores
 * @returns {Object|null} - { average: number, count: number } or null if no scores
 */
export const calculateAverageScoreWithCount = (candidateId, allScores) => {
  const candidateScores = filterCandidateScores(candidateId, allScores);

  if (candidateScores.length === 0) return null;

  const total = candidateScores.reduce((sum, score) => {
    const attributes = score.attributes || score;
    return sum + (attributes.totalScore || 0);
  }, 0);

  return {
    average: total / candidateScores.length,
    count: candidateScores.length,
  };
};

/**
 * Calculate committee median score and count
 * @param {Array} candidateScoresArray - Array of all candidate scores
 * @param {number} committeeId - The committee ID to filter by
 * @returns {Object|null} - { median: number, count: number } or null if no scores
 */
export const calculateCommitteeMedian = (candidateScoresArray, committeeId) => {
  if (
    !candidateScoresArray ||
    candidateScoresArray.length === 0 ||
    !committeeId
  ) {
    return null;
  }

  const candidateAverages = {};

  candidateScoresArray.forEach((score) => {
    const attributes = score.attributes || score;

    let belongsToCommittee = false;

    if (committeeId === CM_PRIMARY_ID) {
      belongsToCommittee =
        attributes.electionTypeId === ELECTION_TYPE.CORRESPONDING_MEMBER;
    } else if (committeeId === SE_COMMITTEE_ID) {
      belongsToCommittee =
        attributes.electionTypeId === ELECTION_TYPE.SPECIAL_ELECTION;
    } else {
      // Regular sectional committees
      belongsToCommittee = attributes.sectionalCommitteeId === committeeId;
    }

    if (
      belongsToCommittee &&
      attributes.totalScore !== null &&
      attributes.totalScore !== undefined
    ) {
      if (!candidateAverages[attributes.candidateId]) {
        candidateAverages[attributes.candidateId] = {
          totalScore: 0,
          count: 0,
        };
      }
      candidateAverages[attributes.candidateId].totalScore +=
        attributes.totalScore;
      candidateAverages[attributes.candidateId].count += 1;
    }
  });

  const averages = Object.values(candidateAverages)
    .map((data) => data.totalScore / data.count)
    .filter((avg) => !Number.isNaN(avg));

  if (averages.length === 0) {
    return null;
  }

  averages.sort((a, b) => a - b);
  const mid = Math.floor(averages.length / 2);

  const median =
    averages.length % 2 === 0
      ? (averages[mid - 1] + averages[mid]) / 2
      : averages[mid];

  const result = {
    median,
    count: averages.length,
  };

  return result;
};

/**
 * Calculate pending scores count for a candidate
 * @param {number} candidateId - The candidate's ID
 * @param {number} committeeId - The committee ID
 * @param {Array} committeeArray - Array of all committees
 * @param {Array} candidateScoresArray - Array of candidate scores
 * @param {Array} coiDeclarationArray - Array of COI declarations
 * @param {string} currentRound - Current nomination round
 * @returns {number} - Count of pending scores
 */
export const calculatePendingScoresCount = (
  candidateId,
  committeeId,
  committeeArray,
  candidateScoresArray,
  coiDeclarationArray,
  currentRound,
) => {
  if (
    !committeeArray ||
    !Array.isArray(committeeArray) ||
    committeeArray.length === 0 ||
    !committeeId
  ) {
    return 0;
  }

  if (!candidateScoresArray || !Array.isArray(candidateScoresArray)) {
    return 0;
  }

  const coiDeclarations = Array.isArray(coiDeclarationArray)
    ? coiDeclarationArray
    : [];

  const committee = committeeArray.find((c) => c.id === committeeId);
  if (!committee) return 0;

  const committeeMembers = [
    ...(committee.attributes?.committeeChair?.data
      ? [{ id: committee.attributes.committeeChair.data.id }]
      : []),
    ...(committee.attributes?.committeeMembers?.data || []).map((member) => ({
      id: member.id,
    })),
  ];

  if (committeeMembers.length === 0) return 0;

  const eligibleMembers = committeeMembers.filter((member) => {
    if (coiDeclarations.length === 0) return true;

    const hasDirectConflict = coiDeclarations.some(
      (coi) =>
        coi.attributes.candidateId === candidateId &&
        coi.attributes.userId === member.id &&
        coi.attributes.nominationRound === currentRound &&
        coi.attributes.coiTypes?.data?.some(
          (coiType) => coiType.attributes.category === 'direct',
        ),
    );

    return !hasDirectConflict;
  });

  const totalExpectedScores = eligibleMembers.length;

  const completedScores = candidateScoresArray.filter((score) => {
    const attributes = score.attributes || score;
    const { isCompleted } = attributes;
    const { totalScore } = attributes;
    const scoreCandidateId = attributes.candidateId;

    return (
      scoreCandidateId === candidateId &&
      isCompleted &&
      totalScore !== null &&
      totalScore !== undefined &&
      totalScore >= 0
    );
  }).length;

  const pendingScores = totalExpectedScores - completedScores;

  return pendingScores > 0 ? pendingScores : 0;
};
